import { z } from "zod";

/**
 * Content Fitness v1.2 assessment vocabulary. Shared by the DB schema
 * (yeet_subcriterion_assessment, content_fitness_subcriteria), the API
 * contracts, the backend accept/consolidation paths and the frontend. Leaf
 * enum module — no imports from db/schema, api, report or queue.
 */

// How a subcriterion is scored. RUBRIC = anchored human judgement,
// OBJECTIVE = derived from listed objectiveChecks, HYBRID = both.
export const CONTENT_FITNESS_ASSESSMENT_MODES = [
  "RUBRIC",
  "OBJECTIVE",
  "HYBRID",
] as const;
export const CONTENT_FITNESS_ASSESSMENT_MODE = {
  RUBRIC: "RUBRIC",
  OBJECTIVE: "OBJECTIVE",
  HYBRID: "HYBRID",
} as const satisfies Record<string, ContentFitnessAssessmentMode>;
export const ContentFitnessAssessmentModeSchema = z.enum(
  CONTENT_FITNESS_ASSESSMENT_MODES,
);
export type ContentFitnessAssessmentMode = z.infer<
  typeof ContentFitnessAssessmentModeSchema
>;

// How much of a dimension's (or the whole framework's) canonical subcriteria
// set has scored assessments. Shared by the rollup, API responses and the
// frontend coverage copy.
export const CONTENT_FITNESS_COVERAGE_STATUSES = [
  "full",
  "partial",
  "unscored",
] as const;
export const CONTENT_FITNESS_COVERAGE_STATUS = {
  FULL: "full",
  PARTIAL: "partial",
  UNSCORED: "unscored",
} as const satisfies Record<string, ContentFitnessCoverageStatus>;
export const ContentFitnessCoverageStatusSchema = z.enum(
  CONTENT_FITNESS_COVERAGE_STATUSES,
);
export type ContentFitnessCoverageStatus = z.infer<
  typeof ContentFitnessCoverageStatusSchema
>;

// Result state of one subcriterion. `scored` carries a bounded 0-4 score;
// `not_applicable` and `unable_to_verify` are explicit absence states excluded
// from roll-ups.
export const CONTENT_FITNESS_VERIFICATION_STATES = [
  "scored",
  "not_applicable",
  "unable_to_verify",
] as const;
export const CONTENT_FITNESS_VERIFICATION_STATE = {
  SCORED: "scored",
  NOT_APPLICABLE: "not_applicable",
  UNABLE_TO_VERIFY: "unable_to_verify",
} as const satisfies Record<string, ContentFitnessVerificationState>;
export const ContentFitnessVerificationStateSchema = z.enum(
  CONTENT_FITNESS_VERIFICATION_STATES,
);
export type ContentFitnessVerificationState = z.infer<
  typeof ContentFitnessVerificationStateSchema
>;

// Coverage states that carry a score. `unscored` is excluded: a website list
// row only reports coverage once something was actually assessed.
export const SCORED_CONTENT_FITNESS_COVERAGE_STATUSES = [
  "full",
  "partial",
] as const satisfies readonly ContentFitnessCoverageStatus[];
export const ScoredContentFitnessCoverageStatusSchema = z.enum(
  SCORED_CONTENT_FITNESS_COVERAGE_STATUSES,
);
export type ScoredContentFitnessCoverageStatus = z.infer<
  typeof ScoredContentFitnessCoverageStatusSchema
>;
